import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useActiveCourse } from '../context/ActiveCourseContext';
import { questionAPI } from '../utils/api';
import Navbar from '../components/Navbar';
import QuestionText from '../components/QuestionText';
import {
  BarChart2, TrendingUp, AlertCircle, BookOpen, ArrowLeft,
  Layers, Hash, Calendar, Award, ChevronRight, Zap, Target,
  FileText, Eye
} from 'lucide-react';
import toast from 'react-hot-toast';

const MasterType = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { activeCourseId, activeCourseData } = useActiveCourse();
  const [analysis, setAnalysis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demoMode, setDemoMode] = useState(false);

  // View State: 'list' | 'detail'
  const [viewMode, setViewMode] = useState('list');
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [activeVariation, setActiveVariation] = useState(null); // Track currently displayed variation
  const [showVariations, setShowVariations] = useState(false);

  useEffect(() => {
    setDemoMode(!isAuthenticated);
    if (activeCourseId) {
      fetchMasterAnalysis();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, activeCourseId]);

  const fetchMasterAnalysis = async () => {
    try {
      const response = await questionAPI.getMasterAnalysis(activeCourseId);
      setAnalysis(response.data.data);
    } catch (error) {
      console.error('Error fetching master analysis:', error);
      console.error('Failed to load analysis');
    } finally {
      setLoading(false);
    }
  };

  // Handle navigation from Filtered page
  useEffect(() => {
    if (location.state?.fromFiltered && location.state?.selectedQuestionId && analysis.length > 0) {
      const questionId = location.state.selectedQuestionId;

      // Find the master question that contains this variation
      const masterItem = analysis.find(item => {
        // Check if this is the master question itself
        if (item.masterQuestion?.uniqueId === questionId) {
          return true;
        }
        // Check if this question is in the variations
        if (item.variations) {
          return item.variations.some(v => v.uniqueId === questionId);
        }
        return false;
      });

      if (masterItem) {
        // Find the specific variation
        const targetVariation = masterItem.masterQuestion?.uniqueId === questionId
          ? masterItem.masterQuestion
          : masterItem.variations?.find(v => v.uniqueId === questionId);

        setSelectedMaster(masterItem);
        setActiveVariation(targetVariation || masterItem.masterQuestion);
        setViewMode('detail');
        setShowVariations(false);
        window.scrollTo(0, 0);

        // Clear the navigation state to prevent re-triggering
        window.history.replaceState({}, document.title);
      } else {
        toast.error('Question not found in master analysis');
      }
    }
  }, [location.state, analysis]);

  // Calculate marking distribution - exact format like "4×3, 2×5"
  const calculateMarkingDistribution = (variations) => {
    if (!variations || variations.length === 0) return '0';

    const marksMap = {};
    variations.forEach(v => {
      const m = v.marks || 0;
      marksMap[m] = (marksMap[m] || 0) + 1;
    });

    return Object.entries(marksMap)
      .sort((a, b) => b[1] - a[1])
      .map(([mark, count]) => `${mark}×${count}`)
      .join(', ');
  };

  // Separate patterns and unique questions
  const patterns = analysis.filter(item =>
    (item.variations && item.variations.length > 1) || item.frequency > 1
  );
  const unique = analysis.filter(item =>
    (!item.variations || item.variations.length === 1) && item.frequency === 1
  );

  // Calculate statistics
  const totalQuestions = analysis.reduce((sum, item) => sum + (item.variations?.length || 0), 0);
  const stats = {
    totalMasterTypes: analysis.length,
    totalPatterns: patterns.length,
    totalUnique: unique.length,
    totalVariations: totalQuestions - unique.length,
    avgFrequency: analysis.length > 0
      ? (analysis.reduce((sum, item) => sum + (item.frequency || 0), 0) / analysis.length).toFixed(1)
      : 0
  };

  const handleViewDetail = (item) => {
    setSelectedMaster(item);
    setActiveVariation(item.masterQuestion); // Initialize with master question
    setViewMode('detail');
    setShowVariations(false);
    window.scrollTo(0, 0);
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedMaster(null);
    setShowVariations(false);
    window.scrollTo(0, 0);
  };

  const handleBackToOverview = () => {
    setViewMode('overview');
    setSelectedMaster(null);
    setShowVariations(false);
    window.scrollTo(0, 0);
  };

  const handleViewList = () => {
    setViewMode('list');
    window.scrollTo(0, 0);
  };

  if (!activeCourseId) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 pt-24 pb-12">
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-400">
            <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-orange-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Active Course</h2>
            <p className="text-gray-600 max-w-md mx-auto">Please activate a course from dashboard.</p>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center pt-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // ==================== OVERVIEW MODE ====================
  if (viewMode === 'overview') {
    return (
      <div className="bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50 min-h-screen">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 pt-24 pb-12 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Master Type Analysis
              {demoMode && (
                <span className="ml-3 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold uppercase tracking-wider">
                  Demo
                </span>
              )}
            </h1>
            {activeCourseData && (
              <div className="inline-flex items-center gap-3 bg-white px-5 py-2 rounded-full border border-gray-400 shadow-sm">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                  {activeCourseData.name?.charAt(0)}
                </div>
                <span className="font-bold text-gray-800">{activeCourseData.name}</span>
              </div>
            )}
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {/* Total Master Types */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <Layers className="w-6 h-6 text-white" />
                </div>
                <span className="text-3xl font-bold text-gray-900">{stats.totalMasterTypes}</span>
              </div>
              <h3 className="text-gray-600 font-medium">Total Master Types</h3>
            </div>

            {/* Total Variations */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <span className="text-3xl font-bold text-gray-900">{stats.totalVariations}</span>
              </div>
              <h3 className="text-gray-600 font-medium">Total Variations</h3>
            </div>

            {/* Patterns */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <span className="text-3xl font-bold text-gray-900">{stats.totalPatterns}</span>
              </div>
              <h3 className="text-gray-600 font-medium">Repeated Patterns</h3>
            </div>

            {/* Unique Questions */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                  <Hash className="w-6 h-6 text-white" />
                </div>
                <span className="text-3xl font-bold text-gray-900">{stats.totalUnique}</span>
              </div>
              <h3 className="text-gray-600 font-medium">Unique Questions</h3>
            </div>
          </div>

          {/* Breakdown Section */}
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <Zap className="w-7 h-7 text-yellow-500" />
              Quick Breakdown
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Patterns Info */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Repeated Patterns</h3>
                </div>
                <p className="text-gray-700 mb-3">
                  Questions that have appeared multiple times across different years or sets.
                  These are high-priority topics to master!
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">
                    {stats.totalPatterns} patterns with {stats.totalVariations - stats.totalUnique} variations
                  </span>
                  <span className="bg-blue-600 text-white px-3 py-1 rounded-lg text-sm font-bold">
                    {stats.totalPatterns > 0 ? Math.round((stats.totalPatterns / stats.totalMasterTypes) * 100) : 0}%
                  </span>
                </div>
              </div>

              {/* Unique Info */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center">
                    <Hash className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Unique Questions</h3>
                </div>
                <p className="text-gray-700 mb-3">
                  Questions that appeared only once. These represent diverse topics and
                  help ensure comprehensive coverage.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">
                    {stats.totalUnique} unique questions
                  </span>
                  <span className="bg-orange-600 text-white px-3 py-1 rounded-lg text-sm font-bold">
                    {stats.totalUnique > 0 ? Math.round((stats.totalUnique / stats.totalMasterTypes) * 100) : 0}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <button
              onClick={handleViewList}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-indigo-700 transition-all hover:-translate-y-1"
            >
              <Eye className="w-6 h-6" />
              View All Questions
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Empty State */}
          {analysis.length === 0 && (
            <div className="bg-white rounded-2xl p-16 text-center shadow-sm border border-gray-400 mt-8">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Questions Found</h3>
              <p className="text-gray-600 max-w-md mx-auto">No master questions available for this course yet.</p>
            </div>
          )}

        </main>
      </div>
    );
  }

  // ==================== DETAIL MODE ====================
  if (viewMode === 'detail' && selectedMaster) {
    // Use activeVariation if set, otherwise fallback to masterQuestion
    const currentQuestion = activeVariation || selectedMaster.masterQuestion;

    const isUnique = !selectedMaster.variations || selectedMaster.variations.length <= 1;
    const theme = isUnique ? 'orange' : 'blue';
    const gradientFrom = isUnique ? 'from-orange-500' : 'from-blue-500';
    const gradientTo = isUnique ? 'to-red-500' : 'to-indigo-500';
    const bgLight = isUnique ? 'bg-orange-50' : 'bg-blue-50';
    const textDark = isUnique ? 'text-orange-700' : 'text-blue-700';
    const borderLight = isUnique ? 'border-orange-200' : 'border-blue-200';

    const yearsRange = selectedMaster.variations && selectedMaster.variations.length > 0
      ? (() => {
        const years = selectedMaster.variations.map(v => v.year).filter(Boolean).sort((a, b) => a - b);
        return years.length > 0 ? `${years[0]} - ${years[years.length - 1]}` : 'N/A';
      })()
      : 'N/A';

    // Filter variations to exclude the currently viewed one
    const timelineVariations = (selectedMaster.variations || []).filter(
      v => v._id !== currentQuestion._id
    );

    return (
      <div className="bg-gray-50 min-h-screen">
        <Navbar />
        <main className="max-w-6xl mx-auto px-4 pt-20 pb-12 sm:px-6 lg:px-8">

          {/* Sticky Header - Full Width Coverage */}
          <div className="sticky top-16 -mx-4 sm:-mx-6 lg:-mx-8 z-40 bg-gray-50/90 backdrop-blur-md py-4 px-4 sm:px-6 lg:px-8 mb-6 border-b border-gray-200 shadow-sm">
            <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
              <button
                onClick={handleBackToList}
                className={`flex items-center gap-2 text-gray-600 hover:${textDark} transition-colors font-semibold px-4 py-2 rounded-lg hover:bg-white/60`}
              >
                <ArrowLeft className="w-5 h-5" />
                Back to List
              </button>

              {/* Sticky Title */}
              <h2 className={`hidden md:flex items-center gap-2 font-mono text-lg font-bold ${textDark}/90`}>
                <Award className="w-5 h-5" />
                {currentQuestion.uniqueId}
              </h2>

              <div className="flex items-center gap-3 text-sm">
                <span className={`${bgLight} ${textDark} px-3 py-1.5 rounded-lg font-bold backdrop-blur-sm`}>
                  {selectedMaster.variations?.length || 0} Variations
                </span>
                <span className="bg-green-100/80 text-green-700 px-3 py-1.5 rounded-lg font-bold backdrop-blur-sm">
                  {yearsRange}
                </span>
              </div>
            </div>
          </div>

          {/* Master Question Card (Dynamic - shows activeVariation) */}
          <div className={`bg-white rounded-3xl p-8 shadow-xl border-2 ${borderLight} mb-8 relative overflow-hidden transition-all duration-500`}>
            <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${gradientFrom} via-${isUnique ? 'orange' : 'indigo'}-500 ${gradientTo}`}></div>

            <div className="flex items-center gap-3 mb-6">
              <div className={`w-14 h-14 bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-2xl flex items-center justify-center text-white shadow-lg`}>
                <Award className="w-7 h-7" />
              </div>
              <div>
                <h2 className={`text-2xl font-bold ${textDark} font-mono`}>{currentQuestion.uniqueId || 'Question'}</h2>
              </div>
            </div>

            <div className="prose prose-lg max-w-none mb-6">
              <QuestionText
                cleanText={currentQuestion.cleanText || currentQuestion.questionText}
                fullTextLatex={currentQuestion.fullTextLatex}
                showLatexByDefault={false}
              />
            </div>

            {/* Detailed Metadata */}
            <div className="space-y-3 pt-6 border-t border-gray-200">
              {/* Row 1: Frequency and Marks */}
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-base text-gray-600">
                  <span className="font-medium text-gray-700">Frequency:</span> <span className={`font-bold text-lg text-red-600`}>{selectedMaster.frequency || selectedMaster.variations?.length || 0}</span>
                </span>
                <span className="w-1.5 h-1.5 bg-gray-300 rounded-full"></span>
                <span className="text-base text-gray-600">
                  <span className="font-medium text-gray-700">Marks:</span> <span className="font-bold text-lg">{currentQuestion.marksPattern || currentQuestion.marks || 'N/A'}</span>
                </span>
              </div>

              {/* Row 2: Subtopic */}
              {currentQuestion.chSubtopic && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-500">Sub-Topic:</span>
                  <span className={`px-2.5 py-1 ${bgLight} ${textDark} rounded-md text-sm font-normal border ${borderLight}`}>
                    {Array.isArray(currentQuestion.chSubtopic)
                      ? currentQuestion.chSubtopic.join(', ')
                      : currentQuestion.chSubtopic}
                  </span>
                </div>
              )}

              {/* Row 3: Tags */}
              {currentQuestion.tags && currentQuestion.tags.length > 0 && (
                <div className="flex items-start gap-2">
                  <span className="text-sm font-semibold text-gray-500 mt-1">Sub-Type:</span>
                  <div className="flex flex-wrap gap-2">
                    {currentQuestion.tags.map((tag, i) => (
                      <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 rounded-md text-xs border border-gray-400">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Show Variations Button/Section - Only for Repeated Patterns */}
          {!isUnique && (
            <>
              <div className="mb-8">
                <button
                  onClick={() => setShowVariations(!showVariations)}
                  className={`w-full py-4 bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white font-bold text-lg rounded-2xl shadow-lg hover:shadow-xl hover:brightness-110 transition-all flex items-center justify-center gap-3`}
                >
                  {showVariations ? 'Hide' : 'Show'} Other Variations
                  <ChevronRight className={`w-5 h-5 transition-transform ${showVariations ? 'rotate-90' : ''}`} />
                </button>
              </div>

              {/* Variations Section */}
              {showVariations && (
                <div className="relative">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <Calendar className={`w-6 h-6 ${textDark}`} />
                    Other Variations ({timelineVariations.length})
                  </h2>

                  {/* Timeline */}
                  <div className={`relative pl-8 border-l-4 border-${isUnique ? 'orange' : 'blue'}-200 space-y-8 ml-4 md:ml-8`}>
                    {timelineVariations.map((variation, idx) => (
                      <div key={idx} className="relative group">
                        {/* Timeline Dot */}
                        <div className={`absolute -left-[45px] top-6 w-6 h-6 rounded-full border-4 border-white bg-${isUnique ? 'orange' : 'blue'}-400 group-hover:bg-${isUnique ? 'orange' : 'blue'}-600 group-hover:scale-125 transition-all shadow-md`}></div>

                        <div
                          onClick={() => {
                            setActiveVariation(variation);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className={`bg-white rounded-2xl p-6 shadow-md border border-gray-400 hover:shadow-xl hover:border-${isUnique ? 'orange' : 'blue'}-300 transition-all cursor-pointer transform hover:-translate-y-1`}
                        >
                          {/* Header - Full Title */}
                          <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-4 border-b border-gray-100">
                            <div className="flex items-center gap-3 flex-1">
                              <h3 className={`font-mono text-base font-bold ${textDark}`}>
                                {variation.uniqueId || 'Unknown ID'}
                              </h3>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="flex items-center gap-1.5 text-sm font-bold text-purple-600 bg-purple-50 px-3 py-1.5 rounded-lg border border-purple-200">
                                <BarChart2 className="w-4 h-4" />
                                {variation.marksPattern || variation.marks || 0} Marks
                              </span>
                            </div>
                          </div>

                          {/* Question Content */}
                          <QuestionText
                            cleanText={variation.cleanText}
                            fullTextLatex={variation.fullTextLatex}
                            showLatexByDefault={false}
                          />

                          {/* Metadata - Subtopic */}
                          {variation.chSubtopic && variation.chSubtopic.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-gray-500">Sub-Topic:</span>
                                <span className={`px-2 py-0.5 ${bgLight} ${textDark} rounded text-xs font-normal border ${borderLight}`}>
                                  {Array.isArray(variation.chSubtopic)
                                    ? variation.chSubtopic.join(', ')
                                    : variation.chSubtopic}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>


                </div>
              )}
            </>
          )}

        </main>
      </div>
    );
  }

  // ==================== LIST MODE ====================
  return (
    <div className="bg-gray-50 min-h-screen">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 pt-24 pb-12 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-3">
              Master Questions
              {demoMode && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold uppercase tracking-wider">
                  Demo
                </span>
              )}
            </h1>

            <div className="flex items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-green-500" />
                <span className="font-bold text-gray-900">{patterns.length}</span> Master Question
              </div>
              <div className="flex items-center gap-2">
                <Hash className="w-4 h-4 text-orange-500" />
                <span className="font-bold text-gray-900">{unique.length}</span> Unique Questions
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-500" />
                <span className="font-bold text-gray-900">{stats.totalVariations}</span> Total Variations
              </div>
            </div>
          </div>

          {activeCourseData && (
            <div className="bg-white px-5 py-3 rounded-xl shadow-sm border border-gray-400 flex items-center gap-3">
              <div>
                <div className="text-xs text-green-600 font-medium uppercase tracking-wide">Active Course</div>
                <div className="font-bold text-gray-900">{activeCourseData.name}</div>
              </div>
            </div>
          )}
        </div>

        {/* Patterns Section */}
        {
          patterns.length > 0 && (
            <section className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 bg-gradient-to-br from-green-500 to-green-600 rounded-xl text-white shadow-md">
                  <Target className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Repeated Patterns</h2>
                  <p className="text-sm text-gray-600">You can Every Practice High-priority Question asked in Different Variations.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {patterns.map((item, index) => (
                  <button
                    key={item._id}
                    onClick={() => handleViewDetail(item)}
                    className="question-card-button bg-white rounded-2xl p-5 shadow-sm border-2 border-gray-400 hover:shadow-xl hover:border-blue-400 hover:-translate-y-1 transition-all duration-300 text-left group relative overflow-hidden"
                  >
                    {/* Gradient accent on hover */}
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-indigo-600 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>

                    {/* Header - Question Identity (Prominent) */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="w-8 h-8 bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 rounded-lg flex items-center justify-center font-bold text-xs shadow-sm group-hover:shadow-md transition-shadow">
                          #{index + 1}
                        </span>
                        <h3 className="font-mono text-base font-bold text-blue-700 group-hover:text-blue-900 transition-colors">
                          {item.masterQuestion?.uniqueId || 'Unknown_ID'}
                        </h3>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                    </div>

                    {/* Question Text */}
                    <div className="mb-3">
                      <p className="text-gray-900 font-medium leading-relaxed line-clamp-3 text-sm group-hover:text-blue-900 transition-colors">
                        <QuestionText
                          cleanText={item.questionText || item.masterQuestion?.cleanText}
                          fullTextLatex={null}
                        />
                      </p>
                    </div>

                    {/* Compact Metadata - Below Question */}
                    <div className="space-y-2 pt-3 border-t border-gray-100">
                      {/* Row 1: Frequency and Marks */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm text-gray-600">
                          <span className="font-medium text-gray-700">Frequency:</span> <span className="font-bold text-base text-red-700">{item.frequency}</span>
                        </span>
                        <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                        <span className="text-sm text-gray-600">
                          <span className="font-medium text-gray-700">Marks:</span> <span className="font-bold text-base">{item.masterQuestion?.marksPattern || item.masterQuestion?.marks || 'N/A'}</span>
                        </span>
                      </div>

                      {/* Row 2: Subtopic */}
                      {item.masterQuestion?.chSubtopic && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-500">Sub-Topic:</span>
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-normal border border-blue-200">
                            {Array.isArray(item.masterQuestion.chSubtopic)
                              ? item.masterQuestion.chSubtopic.join(', ')
                              : item.masterQuestion.chSubtopic}
                          </span>
                        </div>
                      )}

                      {/* Row 3: Tags */}
                      {item.tags && item.tags.length > 0 && (
                        <div className="flex items-start gap-2">
                          <span className="text-xs font-semibold text-gray-500 mt-0.5">Sub-Type:</span>
                          <div className="flex flex-wrap gap-1">
                            {item.tags.slice(0, 4).map((tag, i) => (
                              <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs border border-gray-400">
                                {tag}
                              </span>
                            ))}
                            {item.tags.length > 4 && (
                              <span className="px-1.5 py-0.5 bg-gray-50 text-gray-400 rounded text-xs">
                                +{item.tags.length - 4}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )
        }

        {/* Unique Questions Section */}
        {
          unique.length > 0 && (
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl text-white shadow-md">
                  <Hash className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Unique Questions</h2>
                  <p className="text-sm text-gray-600">Questions that appeared only once</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {unique.map((item, index) => (
                  <button
                    key={item._id}
                    onClick={() => handleViewDetail(item)}
                    className="question-card-button bg-white rounded-2xl p-5 shadow-sm border-2 border-gray-400 hover:shadow-xl hover:border-orange-400 hover:-translate-y-1 transition-all duration-300 text-left group relative overflow-hidden"
                  >
                    {/* Gradient accent on hover */}
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-red-500 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>

                    {/* Header - Question Identity (Prominent) */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="w-8 h-8 bg-gradient-to-br from-orange-50 to-red-50 text-orange-600 rounded-lg flex items-center justify-center font-bold text-xs shadow-sm group-hover:shadow-md transition-shadow">
                          #{index + 1}
                        </span>
                        <h3 className="font-mono text-base font-bold text-orange-700 group-hover:text-orange-900 transition-colors">
                          {item.masterQuestion?.uniqueId || 'Unknown_ID'}
                        </h3>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-orange-600 group-hover:translate-x-1 transition-all" />
                    </div>

                    {/* Question Text */}
                    <div className="mb-3">
                      <p className="text-gray-900 font-medium leading-relaxed line-clamp-3 text-sm group-hover:text-orange-900 transition-colors">
                        <QuestionText
                          cleanText={item.questionText || item.masterQuestion?.cleanText}
                          fullTextLatex={null}
                        />
                      </p>
                    </div>

                    {/* Compact Metadata - Below Question (No Frequency) */}
                    <div className="space-y-2 pt-3 border-t border-gray-100">
                      {/* Row 1: Marks Only */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm text-gray-600">
                          <span className="font-medium text-gray-700">Marks:</span> <span className="font-bold text-base">{item.masterQuestion?.marksPattern || item.masterQuestion?.marks || 'N/A'}</span>
                        </span>
                      </div>

                      {/* Row 2: Subtopic */}
                      {item.masterQuestion?.chSubtopic && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-500">Sub-Topic:</span>
                          <span className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded text-xs font-normal border border-orange-200">
                            {Array.isArray(item.masterQuestion.chSubtopic)
                              ? item.masterQuestion.chSubtopic.join(', ')
                              : item.masterQuestion.chSubtopic}
                          </span>
                        </div>
                      )}

                      {/* Row 3: Tags */}
                      {item.tags && item.tags.length > 0 && (
                        <div className="flex items-start gap-2">
                          <span className="text-xs font-semibold text-gray-500 mt-0.5">Sub-Type:</span>
                          <div className="flex flex-wrap gap-1">
                            {item.tags.slice(0, 4).map((tag, i) => (
                              <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs border border-gray-400">
                                {tag}
                              </span>
                            ))}
                            {item.tags.length > 4 && (
                              <span className="px-1.5 py-0.5 bg-gray-50 text-gray-400 rounded text-xs">
                                +{item.tags.length - 4}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )
        }

        {/* Empty State */}
        {
          analysis.length === 0 && (
            <div className="bg-white rounded-2xl p-16 text-center shadow-sm border border-gray-400">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Questions Found</h3>
              <p className="text-gray-600 max-w-md mx-auto">No master questions available for this course yet.</p>
            </div>
          )
        }
      </main >
    </div >
  );
};

export default MasterType;
